import React, { useRef, useState, useCallback } from 'react';

interface VideoPlayerProps {
  videoUrl: string;
  title?: string;
  description?: string;
}

function isInstagramUrl(url: string): boolean {
  return url.includes('instagram.com');
}

function isYouTubeUrl(url: string): boolean {
  return url.includes('youtube.com') || url.includes('youtu.be');
}

function getInstagramEmbedUrl(url: string): string {
  // If it's already an embed URL, return as is
  if (url.includes('/embed')) {
    return url;
  }

  // Convert Instagram URL to embed URL
  const match = url.match(/instagram\.com\/p\/([a-zA-Z0-9_-]+)/);
  if (match) {
    return `https://www.instagram.com/p/${match[1]}/embed/`;
  }

  // If it's a different Instagram URL format, try to construct embed URL
  const reelMatch = url.match(/instagram\.com\/reel\/([a-zA-Z0-9_-]+)/);
  if (reelMatch) {
    return `https://www.instagram.com/reel/${reelMatch[1]}/embed/`;
  }

  return url;
}

function getYouTubeEmbedUrl(url: string): string {
  // If it's already an embed URL, return as is
  if (url.includes('/embed/')) {
    return url;
  }

  // Extract video ID from YouTube URL
  let videoId = '';

  // Handle youtu.be short URLs
  if (url.includes('youtu.be/')) {
    const match = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (match) {
      videoId = match[1];
    }
  }
  // Handle youtube.com URLs
  else if (url.includes('youtube.com/watch')) {
    const match = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
    if (match) {
      videoId = match[1];
    }
  }
  // Handle youtube.com/embed URLs
  else if (url.includes('youtube.com/embed/')) {
    const match = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/);
    if (match) {
      videoId = match[1];
    }
  }

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }

  return url;
}

export function VideoPlayer({ videoUrl, title }: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const isInstagram = isInstagramUrl(videoUrl);
  const isYouTube = isYouTubeUrl(videoUrl);

  let embedUrl = videoUrl;
  if (isInstagram) {
    embedUrl = getInstagramEmbedUrl(videoUrl);
  } else if (isYouTube) {
    embedUrl = getYouTubeEmbedUrl(videoUrl);
  }

  // Intentar autoplay con sonido después de interacción del usuario
  const handleUserInteraction = useCallback(() => {
    if (!hasUserInteracted && iframeRef.current) {
      setHasUserInteracted(true);
      // Recargar el iframe con sonido habilitado
      const currentSrc = iframeRef.current.src;
      if (currentSrc.includes('mute=1') || !currentSrc.includes('mute=')) {
        iframeRef.current.src = currentSrc.replace('mute=1', 'mute=0').replace('&autoplay=1', '&autoplay=1&mute=0');
      }
    }
  }, [hasUserInteracted]);

  // Agregar event listeners para detectar interacción del usuario
  React.useEffect(() => {
    const handleInteraction = () => handleUserInteraction();

    if (isYouTube) {
      document.addEventListener('click', handleInteraction, { once: true });
      document.addEventListener('scroll', handleInteraction, { once: true });
      document.addEventListener('keydown', handleInteraction, { once: true });
    }

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [isYouTube, hasUserInteracted, handleUserInteraction]);

  return (
    <React.Fragment>
      <div
        ref={containerRef}
        className="relative w-full mx-auto"
        onClick={handleUserInteraction}
      >
        {isInstagram ? (
          <div className="w-full max-w-md mx-auto">
            <div className="relative w-full" style={{ aspectRatio: '9/16', maxWidth: '400px', margin: '0 auto' }}>
              <iframe
                ref={iframeRef}
                src={embedUrl}
                className="w-full h-full border-0 rounded-2xl"
                frameBorder="0"
                scrolling="no"
                loading="lazy"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                title={title || "Instagram Video"}
              />
            </div>
          </div>
        ) : isYouTube ? (
          <div className="w-full">
            <div className="relative w-full" style={{ aspectRatio: '16/9', maxWidth: '1000px', margin: '0 auto' }}>
              <iframe
                ref={iframeRef}
                src={`${embedUrl}?autoplay=1&mute=1&rel=0&modestbranding=1&showinfo=0&controls=1&autohide=1&start=0&enablejsapi=1`}
                className="w-full h-full border-0 rounded-xl shadow-2xl"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                title={title || "YouTube Video"}
                loading="lazy"
                style={{
                  filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.3))',
                  borderRadius: '16px'
                }}
              />
            </div>
            {!hasUserInteracted && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-xl">
                <div className="text-center text-white">
                  <div className="w-16 h-16 mx-auto mb-4 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <p className="text-lg font-semibold">Haz clic para activar el sonido</p>
                  <p className="text-sm opacity-90">El video se reproduce automáticamente</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full">
            <div className="relative w-full" style={{ aspectRatio: '16/9', maxWidth: '900px', margin: '0 auto' }}>
              <video
                className="w-full h-full border-0 rounded-xl shadow-2xl"
                src={videoUrl}
                controls={true}
                loop={false}
                autoPlay={true}
                muted={false}
                playsInline
                preload="metadata"
                style={{
                  filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.3))',
                  borderRadius: '16px'
                }}
              />
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}